// ui/src/utils/logger.js
/**
 * Production-ready logging utility that replaces console statements
 * In development: logs to console
 * In production: silences debug logs, only shows errors
 * Also integrates with Highlight.io for error tracking
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isTest = process.env.NODE_ENV === 'test';

// Lazy load Highlight to avoid circular dependencies
let H = null;
const getHighlight = () => {
  if (!H && !isTest) {
    try {
      // Import H from highlight.run directly to avoid circular import with our highlight.js
      H = require('highlight.run').H;
    } catch (e) {
      // Highlight not available, continue without it
    }
  }
  return H;
};

class Logger {
  constructor() {
    this.isDevelopment = isDevelopment;
    this.isTest = isTest;
  }

  /**
   * Debug logging - only shown in development
   */
  debug(...args) {
    if (this.isDevelopment && !this.isTest) {
      console.log('[DEBUG]', ...args);
    }
  }

  /**
   * Info logging - shown in development only
   */
  info(...args) {
    if (this.isDevelopment && !this.isTest) {
      console.info('[INFO]', ...args);
    }
  }

  /**
   * Warning logging - shown in all environments
   */
  warn(...args) {
    if (!this.isTest) {
      console.warn('[WARN]', ...args);
    }
  }

  /**
   * Error logging - shown in all environments
   */
  error(...args) {
    if (!this.isTest) {
      console.error('[ERROR]', ...args);
      
      // Send errors to Highlight.io
      const highlight = getHighlight();
      if (highlight) {
        try {
          // If first argument is an Error object, use it directly
          if (args[0] instanceof Error) {
            highlight.consumeError(args[0], { type: 'custom' });
          } else {
            // Create an error from the message
            const errorMessage = args.join(' ');
            highlight.consumeError(new Error(errorMessage), { type: 'custom' });
          }
        } catch (e) {
          // Don't fail if Highlight is not available
        }
      }
    }
  }

  /**
   * Production logging - always shown (for critical info)
   */
  production(...args) {
    if (!this.isTest) {
      console.log('[PROD]', ...args);
    }
  }
}

export const logger = new Logger();
export default logger;
